import React, { useRef } from 'react';

import {
	Building2,
	Loader2,
	Lock as LockIcon,
	Save,
	Upload,
} from 'lucide-react';

import {
	useCreateEnterpriseSettingsMutation,
	useGetEnterpriseSettingsQuery,
	useUpdateEnterpriseSettingsMutation,
} from '../../api';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../providers/AppAuthProvider';
import { Button, Card, CardBody, Input, Typography } from '../common';

import { useEnterpriseSettingsForm } from './hooks';

interface EnterpriseSettingsProps {
	onBack?: () => void;
}

const EnterpriseSettings: React.FC<EnterpriseSettingsProps> = ({ onBack }) => {
	const { showToast } = useToast();
	const { isAdmin } = useAuth();
	const { data: settings, isLoading } = useGetEnterpriseSettingsQuery();
	const [createSettings, { isLoading: isCreating }] =
		useCreateEnterpriseSettingsMutation();
	const [updateSettings, { isLoading: isUpdating }] =
		useUpdateEnterpriseSettingsMutation();

	const [state, dispatch] = useEnterpriseSettingsForm(settings);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			// Validate file type
			if (!file.type.startsWith('image/')) {
				showToast('Invalid File', 'Please select a valid image file', 'error');
				// Reset file input
				if (fileInputRef.current) {
					fileInputRef.current.value = '';
				}
				return;
			}

			// Validate file size (max 2MB)
			if (file.size > 2 * 1024 * 1024) {
				showToast('File Too Large', 'File size must be less than 2MB', 'error');
				// Reset file input
				if (fileInputRef.current) {
					fileInputRef.current.value = '';
				}
				return;
			}

			const reader = new FileReader();
			reader.onloadend = () => {
				dispatch({
					type: 'SET_LOGO',
					file,
					preview: reader.result as string,
				});
			};
			reader.readAsDataURL(file);
		}
	};

	const handleSave = async () => {
		try {
			// Create FormData
			const formData = new FormData();
			formData.append('company_name', state.companyName);
			if (state.logoFile) {
				formData.append('logo', state.logoFile);
			}
			if (state.addressStreet)
				formData.append('address_street', state.addressStreet);
			if (state.addressCity) formData.append('address_city', state.addressCity);
			if (state.addressState)
				formData.append('address_state', state.addressState);
			if (state.addressZip) formData.append('address_zip', state.addressZip);
			if (state.addressCountry)
				formData.append('address_country', state.addressCountry);
			if (state.contactPhone)
				formData.append('contact_phone', state.contactPhone);
			if (state.contactEmail)
				formData.append('contact_email', state.contactEmail);
			if (state.contactWebsite)
				formData.append('contact_website', state.contactWebsite);

			if (settings) {
				// Update using PATCH
				await updateSettings(formData).unwrap();
				showToast(
					'Settings Updated',
					'Organization settings have been updated successfully',
					'success',
				);
			} else {
				// Create using POST
				await createSettings(formData).unwrap();
				showToast(
					'Settings Created',
					'Organization settings have been created successfully',
					'success',
				);
			}

			dispatch({ type: 'RESET_CHANGES' });
			// Reset file input
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
		} catch (err: any) {
			console.error('Failed to save settings:', err);
			showToast(
				'Save Failed',
				err?.data?.message || 'Failed to save settings. Please try again.',
				'error',
			);
		}
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center min-h-[400px]">
				<div className="flex flex-col items-center space-y-4">
					<Loader2 className="h-8 w-8 animate-spin text-primary-600" />
					<Typography variant="body2" color="muted">
						Loading organization settings...
					</Typography>
				</div>
			</div>
		);
	}

	const isSaving = isCreating || isUpdating;

	return (
		<div className="space-y-6">
			{/* Header Card */}
			<Card>
				<CardBody>
					<div className="flex items-center justify-between">
						<div className="flex items-center space-x-3">
							<div className="p-2 bg-primary-100 rounded-lg">
								<Building2 className="h-6 w-6 text-primary-600" />
							</div>
							<div>
								<Typography variant="h4">Organization Settings</Typography>
								<Typography variant="body2" color="muted">
									Configure your company branding for PDF reports
								</Typography>
							</div>
						</div>
						{!isAdmin && (
							<div className="flex items-center space-x-2 text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
								<LockIcon className="h-4 w-4" />
								<Typography variant="caption">Read Only</Typography>
							</div>
						)}
					</div>
				</CardBody>
			</Card>

			{/* Settings Form */}
			<Card>
				<CardBody>
					<div className="space-y-6">
						{/* Company Logo and Name Section */}
						<div>
							<label className="block text-sm font-medium text-neutral-700 mb-3">
								Company Branding
							</label>
							<div className="flex items-start space-x-6">
								{/* Logo Upload */}
								<div className="flex-shrink-0">
									<div
										className={`relative w-32 h-32 border-2 border-neutral-200 rounded-lg flex items-center justify-center bg-neutral-50 overflow-hidden ${isAdmin ? 'cursor-pointer group' : 'cursor-not-allowed opacity-75'}`}
										onClick={(e) => {
											if (!isAdmin) return;
											e.preventDefault();
											fileInputRef.current?.click();
										}}
									>
										{state.logoPreview ? (
											<>
												<img
													src={state.logoPreview}
													alt="Logo preview"
													className="max-w-full max-h-full object-contain"
												/>
												{/* Hover overlay */}
												<div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
													<div className="text-white text-center">
														<Upload className="h-6 w-6 mx-auto mb-1" />
														<span className="text-xs font-medium">
															Change Logo
														</span>
													</div>
												</div>
											</>
										) : (
											<div className="text-center">
												<Upload className="h-8 w-8 text-neutral-400 mx-auto mb-2" />
												<span className="text-xs text-neutral-500 font-medium">
													Upload Logo
												</span>
											</div>
										)}
									</div>
									<input
										ref={fileInputRef}
										type="file"
										accept="image/*"
										onChange={handleLogoSelect}
										className="hidden"
									/>
									<Typography
										variant="caption"
										color="muted"
										className="block mt-2 text-center"
									>
										PNG/JPEG, max 2MB
									</Typography>
								</div>

								{/* Company Name */}
								<div className="flex-1">
									<Input
										label="Company Name"
										value={state.companyName}
										onChange={(e) =>
											dispatch({
												type: 'SET_FIELD',
												field: 'companyName',
												value: e.target.value,
											})
										}
										placeholder="Enter company name"
										required
										fullWidth
										disabled={!isAdmin}
									/>
								</div>
							</div>
						</div>

						{/* Address Section */}
						<div>
							<Typography variant="h5" className="mb-4">
								Physical Address
							</Typography>
							<div className="space-y-4">
								<Input
									label="Street Address"
									value={state.addressStreet}
									onChange={(e) =>
										dispatch({
											type: 'SET_FIELD',
											field: 'addressStreet',
											value: e.target.value,
										})
									}
									placeholder="123 Main Street"
									fullWidth
									disabled={!isAdmin}
								/>
								<div className="grid grid-cols-2 gap-4">
									<Input
										label="City"
										value={state.addressCity}
										onChange={(e) =>
											dispatch({
												type: 'SET_FIELD',
												field: 'addressCity',
												value: e.target.value,
											})
										}
										placeholder="San Francisco"
										fullWidth
										disabled={!isAdmin}
									/>
									<Input
										label="State"
										value={state.addressState}
										onChange={(e) =>
											dispatch({
												type: 'SET_FIELD',
												field: 'addressState',
												value: e.target.value,
											})
										}
										placeholder="CA"
										fullWidth
										disabled={!isAdmin}
									/>
								</div>
								<div className="grid grid-cols-2 gap-4">
									<Input
										label="ZIP Code"
										value={state.addressZip}
										onChange={(e) =>
											dispatch({
												type: 'SET_FIELD',
												field: 'addressZip',
												value: e.target.value,
											})
										}
										placeholder="94105"
										fullWidth
										disabled={!isAdmin}
									/>
									<Input
										label="Country"
										value={state.addressCountry}
										onChange={(e) =>
											dispatch({
												type: 'SET_FIELD',
												field: 'addressCountry',
												value: e.target.value,
											})
										}
										placeholder="USA"
										fullWidth
										disabled={!isAdmin}
									/>
								</div>
							</div>
						</div>

						{/* Contact Section */}
						<div>
							<Typography variant="h5" className="mb-4">
								Contact Information
							</Typography>
							<div className="space-y-4">
								<Input
									label="Phone Number"
									type="tel"
									value={state.contactPhone}
									onChange={(e) =>
										dispatch({
											type: 'SET_FIELD',
											field: 'contactPhone',
											value: e.target.value,
										})
									}
									placeholder="(555) 123-4567"
									fullWidth
									disabled={!isAdmin}
								/>
								<Input
									label="Email"
									type="email"
									value={state.contactEmail}
									onChange={(e) =>
										dispatch({
											type: 'SET_FIELD',
											field: 'contactEmail',
											value: e.target.value,
										})
									}
									placeholder="contact@company.com"
									fullWidth
									disabled={!isAdmin}
								/>
								<Input
									label="Website"
									type="url"
									value={state.contactWebsite}
									onChange={(e) =>
										dispatch({
											type: 'SET_FIELD',
											field: 'contactWebsite',
											value: e.target.value,
										})
									}
									placeholder="https://www.company.com"
									fullWidth
									disabled={!isAdmin}
								/>
							</div>
						</div>

						{/* Save Button */}
						{isAdmin && (
							<div className="flex justify-end space-x-4 pt-4 border-t border-neutral-200">
								<Button
									variant="primary"
									icon={Save}
									onClick={handleSave}
									disabled={
										!state.hasChanges || !state.companyName.trim() || isSaving
									}
									loading={isSaving}
								>
									{isSaving ? 'Saving...' : 'Save Changes'}
								</Button>
							</div>
						)}
					</div>
				</CardBody>
			</Card>
		</div>
	);
};

export default EnterpriseSettings;
